import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL, API_ENDPOINTS } from "../config/constants";

const Download = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("Preparing your download...");

  useEffect(() => {
    const initiateDownload = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Invalid download link. Token is missing.");
        return;
      }

      if (!orderId) {
        setStatus("error");
        setMessage("Invalid download link. Order ID is missing.");
        return;
      }

      try {
        // Use centralized API configuration
        const downloadUrl = `${API_BASE_URL}${API_ENDPOINTS.PUBLIC_DOWNLOAD(orderId)}?token=${token}`;

        // Fetch the file
        const response = await axios.get(downloadUrl, {
          responseType: "blob",
        });

        // Extract filename from header
        let filename = `remedy_${orderId}.pdf`;
        const disposition = response.headers["content-disposition"];
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }

        // Create blob and trigger download
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        setStatus("success");
        setMessage("Your download has started! You can close this page.");

      } catch (error) {
        console.error("Download error:", error);
        
        if (error.response?.status === 403) {
          setStatus("error");
          setMessage("This download link has expired or has already been used. Please request a new download link.");
        } else if (error.response?.status === 404) {
          setStatus("error");
          setMessage("File not found. Please contact support.");
        } else {
          setStatus("error");
          setMessage("Download failed. Please try again or contact support.");
        }
      }
    };

    initiateDownload();
  }, [orderId, token]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === "loading" && (
          <>
            <div style={styles.spinner}></div>
            <h2 style={styles.title}>{message}</h2>
          </>
        )}
        
        {status === "success" && (
          <>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.title}>Download Started!</h2>
            <p style={styles.message}>{message}</p>
            <button 
              style={styles.button}
              onClick={() => window.close()}
            >
              Close Window
            </button>
          </>
        )}
        
        {status === "error" && (
          <>
            <div style={styles.errorIcon}>✕</div>
            <h2 style={styles.title}>Download Failed</h2>
            <p style={styles.message}>{message}</p>
            <button 
              style={styles.button}
              onClick={() => window.close()}
            >
              Close Window
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f3f4f6",
    padding: "20px",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "40px",
    maxWidth: "500px",
    width: "100%",
    textAlign: "center",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },
  spinner: {
    margin: "0 auto 20px",
    width: "50px",
    height: "50px",
    border: "4px solid #f3f4f6",
    borderTop: "4px solid #3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  successIcon: {
    width: "60px",
    height: "60px",
    margin: "0 auto 20px",
    backgroundColor: "#10b981",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: "bold",
  },
  errorIcon: {
    width: "60px",
    height: "60px",
    margin: "0 auto 20px",
    backgroundColor: "#ef4444",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: "bold",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: "12px",
  },
  message: {
    fontSize: "16px",
    color: "#6b7280",
    marginBottom: "24px",
    lineHeight: "1.5",
  },
  button: {
    backgroundColor: "#3b82f6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default Download;
